/** A "source" is one of the org's uploaded documents. */
export interface SourceDoc {
  _id: string;
  title: string;
  fileName: string;
  fileUrl?: string;
  mimeType?: string;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  processingStage?: 'extracting' | 'indexing';
  errorMessage?: string;
  createdAt: string;
}

/** The file's extension for the source tile (PDF, DOCX, DOC …), at most four letters. */
export const extLabel = (fileName: string): string => {
  const ext = fileName.includes('.') ? fileName.split('.').pop()?.toUpperCase() : undefined;
  return (ext || 'DOC').slice(0, 4);
};

/** `POST /api/chat` accepts 1..25 source ids; a notebook can hold more, so the chat's scope is capped here. */
export const MAX_CHAT_SOURCES = 25;

/** The chat's scope: the checked sources that are indexed (still-processing ones join once ready), capped for the endpoint. */
export const pickChatSources = (documents: SourceDoc[], selectedIds: ReadonlySet<string>): SourceDoc[] =>
  documents.filter((d) => selectedIds.has(d._id) && d.processingStatus === 'completed').slice(0, MAX_CHAT_SOURCES);

/** What the first load and "select all" tick: the first MAX sources in the order the API returned them. */
export const defaultSelection = (documents: SourceDoc[]): Set<string> =>
  new Set(documents.slice(0, MAX_CHAT_SOURCES).map((d) => d._id));

/** Adds a source to the ticked set, refusing it when the set is already at the cap (the caller says so). */
export const withSource = (selected: ReadonlySet<string>, id: string): Set<string> => {
  const next = new Set(selected);
  if (next.size < MAX_CHAT_SOURCES) next.add(id);
  return next;
};

const isReady = (doc: SourceDoc): boolean => doc.processingStatus === 'completed';

/** "Select all": clear when everything that can be ticked already is, otherwise tick up to the cap. */
export const toggleAllSelection = (documents: SourceDoc[], selected: ReadonlySet<string>): Set<string> => {
  const tickable = documents.slice(0, MAX_CHAT_SOURCES).filter(isReady);
  return tickable.length > 0 && tickable.every((d) => selected.has(d._id)) ? new Set() : defaultSelection(documents);
};

/** The "select all" box: checked when every indexed source that fits under the cap is ticked, a dash when only some are. */
export const selectAllState = (documents: SourceDoc[], selected: ReadonlySet<string>): boolean | 'indeterminate' => {
  const anyTicked = documents.some((d) => isReady(d) && selected.has(d._id));
  if (!anyTicked) return false;
  const tickable = documents.slice(0, MAX_CHAT_SOURCES).filter(isReady);
  return tickable.every((d) => selected.has(d._id)) ? true : 'indeterminate';
};
